import { Feature, Point } from 'geojson';

import { SnMapClusterPoint } from '@lib/api';
import { colorIsDark } from '@lib/common/helpers';
import { SnLonLat } from '@lib/geo/helpers';
import { COLORS } from '@lib/ui/theme';
import fususIcons from 'libs/ui/icon/src/lib/components/fusus-icons/service/icons.json';

import { IGoogleMapMarkerIconLabel, IGoogleMapMarkerLabel } from '..';
import { GoogleMap } from '../../map';
import { FsAdvancedMarker } from './advanced-marker';
import { AxonNotificationIconService } from '@lib/axon/services/axon-notification-icon.service';

export interface IAdvancedIconOptions {
  fontSet?: string;
  fontIcon?: string;
  color?: string;
  url?: string;
}
export interface IAdvancedStyleOptions {
  strokeOpacity?: number;
  strokeWeight?: number;
  strokeColor?: string;
  fillOpacity?: number;
  fillColor?: string;
  borderRadius?: string;
  overrideColor?: boolean;
  scale?: number;
}
export interface IAdvancedBadgeOptions {
  top?: string;
  left?: string;
  bottom?: string;
  right?: string;
}

export interface IAdvancedFeatureOptions {
  icon?: IAdvancedIconOptions;
  title?: string;
  draggable?: boolean;
  zIndex?: number;
  feature?: Feature<Point>;
  id?: string;
  position?: google.maps.LatLng;
  iconLabel?: IGoogleMapMarkerIconLabel;
  size?: string;
  color?: string;
  rotation?: number;
  outline?: boolean;
  map?: GoogleMap;
  styles?: IAdvancedStyleOptions;
  isAxonLayer?: boolean;
  isCoreMobile?: boolean;
  isSkydio?: boolean;
  cluster?: SnMapClusterPoint;
  content?: any;
  badgePosition?: IAdvancedBadgeOptions;
  layer?: string;
  organization?: string;
  isAxonDevice?: boolean;
}

export class FsAdvancedFeatureMarker extends FsAdvancedMarker {
  public updated: number = Date.now();
  public id: string;
  public feature: Feature<Point>;
  public icon: IAdvancedIconOptions;
  public size: string;
  public color: string;
  public rotation: number;
  public outline: boolean;
  public iconLabel: IGoogleMapMarkerIconLabel;
  public styles: IAdvancedStyleOptions;
  public cluster: SnMapClusterPoint;
  public content = document.createElement('div');
  public badgePosition: IAdvancedBadgeOptions;
  public layer: string;
  public organization: string;

  private _options: IAdvancedFeatureOptions;
  private _destination: google.maps.LatLng;
  private _distanceToDestination: number;
  private _deltaLat: number;
  private _deltaLng: number;
  private _followMode: boolean;
  private _expectedMoves: number;
  private _actualMoves: number;
  private _icons: any;

  constructor(public options?: IAdvancedFeatureOptions, _axonNotificationIconService?: AxonNotificationIconService) {
    super(
      {
        position: options?.position,
        title: options?.title,
        gmpDraggable: options.draggable,
        collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
        zIndex: options.zIndex,
      },
      null,
      options.cluster,
      _axonNotificationIconService,
    );
    this._options = {
      size: '24px' || options.size,
      color: options.icon?.color ? options.icon?.color : '#fff',
      rotation: options.rotation || 0,
      outline: this.outline || false,
      ...options,
    };

    if (this._options.map) {
      this.addTo(this._options.map);
    }

    this.id = this._options.id;
    this.feature = this._options.feature;
    this.icon = this._options.icon;
    this.size = this._options.size;
    this.color = this._options.color;
    this.rotation = this._options.rotation;
    this.iconLabel = this._options.iconLabel;
    this.styles = this._options.styles;
    this.cluster = this._options.cluster;
    this.content = this._options.content || this.content;
    this.outline = this._options.outline;
    this.layer = this._options.layer;
    this.organization = this._options.organization;
    this.badgePosition = {
      right: '-6px',
      bottom: '-2px',
      top: 'auto',
      left: 'auto',
      ...this._options.badgePosition,
    };
    if (this.cluster && this._options.isAxonLayer) {
      this.content.innerHTML = `
        <span class="fs ab3"></span>
      `;
      this.content.classList.add('fs__advanced-axon-cluster-marker');
      this.getMarker().content = this.content;
      this.createBadge();
      return;
    }
    if (this.icon?.url) {
      this.content.innerHTML = `
        <img src="${this.icon.url}" />
      `;
      this.content.classList.add('fs__center-marker');
      this.content.classList.add('fs__advanced-marker');
      this.content.classList.add('fs__advanced-feature-image-marker');
      this.getMarker().content = this.content;
      return;
    }
    if (!this.icon?.fontSet || !this.icon?.fontIcon) {
      this.getMarker().content = new google.maps.marker.PinElement().element;
      return;
    }
    let icon;
    if (this.icon?.fontSet === 'fs') {
      this._icons = fususIcons.icons;
      const paths = this.getPaths(this.icon.fontIcon);
      icon = document.createElement('span');
      icon.classList.value = `fs ${this.icon.fontIcon}`;
      icon.style.fontSize = this.size;
      paths.forEach((el, i) => {
        const t = document.createElement('span');
        t.classList.value = `path${i + 1}`;
        icon?.appendChild(t);
      });
      icon.classList.add('fs__advanced-feature-icon');
      this.content.appendChild(icon);
    } else {
      this.content.innerHTML = `
      <i class="${this._toFaSet(this.icon.fontSet)} ${this.icon.fontIcon}" style="font-size: ${
        this.size
      }; color: ${this.color}; transform: rotate(${this.rotation}deg);"></i>
      `;
    }
    if (this.iconLabel?.text) {
      const iconLabel = document.createElement('span');
      iconLabel.innerText = this.iconLabel.text;
      this.content.appendChild(iconLabel);
      iconLabel.classList.add('fs__advanced-feature-marker-label');
      iconLabel.style.top = `${this.iconLabel.top || 0}px`;
      iconLabel.style.left = `${this.iconLabel.left || 0}px`;
    }
    if (this.styles) {
      this.setStyles(this.content);
    }
    if (this.outline) {
      this.content.classList.add('fs__advanced-marker-outline');
    }
    this.content.classList.add('fs__advanced-feature-marker');
    if (this._options.isAxonLayer && icon) {
      const status =
        this.feature?.properties?.status?.toLowerCase() ||
        this.feature?.properties?.attributes?.status?.toLowerCase();
      if (status === 'recording') {
        this.content.classList.add('fs__axon-recording-status');
      } else {
        this.content.classList.remove('fs__axon-recording-status');
      }
    }
    this.content.classList.add('fs__center-marker');
    this.content.classList.add('fs__advanced-marker');
    this.getMarker().content = this.content;
  }

  public setDestination(latLng: google.maps.LatLng): void {
    if (this._destination) {
      this.getMarker().position = this._destination;
    }
    this._destination = latLng;
  }

  public setFollowMode(followMode: boolean): void {
    this._followMode = followMode;
  }

  public getFollowMode(): boolean {
    return this._followMode;
  }

  public setDeltas(seconds: number): void {
    const destination = this._destination;
    const current = this.getLonLat();
    this._expectedMoves = 10 * seconds;
    this._actualMoves = 0;
    this._deltaLat = (destination.lat() - current.lat) / this._expectedMoves;
    this._deltaLng = (destination.lng() - current.lon) / this._expectedMoves;
  }

  public setLabel(label: string): void {
    this.getMarker().title = label;
    if (this.options.iconLabel?.text) {
      this._options.iconLabel.text = label;
    }
  }

  public getLabel(): IGoogleMapMarkerLabel | undefined {
    return this.iconLabel ? { text: this.iconLabel.text } : undefined;
  }

  public setFeatures(features: any): void {
    this.feature = features;
    if (this.iconLabel?.text) {
      const existingLabel: HTMLElement = this.content.querySelector(
        '.fs__advanced-feature-marker-label',
      );
      if (existingLabel) {
        existingLabel.innerText = this.iconLabel.text;
        existingLabel.style.top = `${this.iconLabel.top || 0}px`;
        existingLabel.style.left = `${this.iconLabel.left || 0}px`;
      } else {
        const iconLabel = document.createElement('span');
        iconLabel.classList.add('fs__advanced-feature-marker-label');
        iconLabel.innerText = this.iconLabel.text;
        this.content.appendChild(iconLabel);
        iconLabel.style.top = `${this.iconLabel.top || 0}px`;
        iconLabel.style.left = `${this.iconLabel.left || 0}px`;
      }
    }
  }

  public animateMap(position: google.maps.LatLng): void {
    const lonLat = new SnLonLat(position.lng(), position.lat());
    this.map.panTo(lonLat);
  }

  public move(): void {
    if (this._actualMoves >= this._expectedMoves) {
      const lonLat = new SnLonLat(this._destination.lng(), this._destination.lat());
      this.setLonLat(lonLat);
      if (this._followMode) {
        this.map.panTo(lonLat);
      }
      return;
    }
    this._actualMoves++;
    const current = this.getLonLat();
    const destination = this._destination;
    const meters2Miles = 0.000621371192;
    this._distanceToDestination =
      Math.round(
        google.maps.geometry.spherical.computeDistanceBetween(
          { lat: current.lat, lng: current.lon },
          this._destination,
        ) *
          meters2Miles *
          10,
      ) / 10;
    if (!this.map?.contains(current)) {
      this._actualMoves = this._expectedMoves;
      const lonLat = new SnLonLat(destination.lng(), destination.lat());
      this.setLonLat(lonLat);
      if (this._followMode) {
        this.map.panTo(lonLat);
      }
    }
    let newLat = current.lat + this._deltaLat;
    if (current.lat === destination.lat()) {
      newLat = destination.lat();
    }
    let newLng = current.lon + this._deltaLng;
    if (current.lon === destination.lng()) {
      newLng = destination.lng();
    }
    const newLonLat = new SnLonLat(newLng, newLat);
    this.setLonLat(newLonLat);
    if (this._followMode) {
      this.map.panTo(newLonLat);
    }
    if (this._distanceToDestination > 0.1) {
      this.moveToDestination();
    }
  }

  public moveToDestination(): void {
    if (!this._destination) {
      return;
    }
    this.setLonLat(new SnLonLat(this._destination.lng(), this._destination.lat()));
  }

  public setFAMarkerRotation(rotation: number): void {
    const el = this.content.querySelector('i');
    if (el) {
      el.style.transform = `rotate(${rotation}deg)`;
    }
  }

  public setFAMarkerStyle(styleType: string, style: string): void {
    const el = this.content.querySelector('i');
    if (el) {
      el.style[styleType] = style;
    }
  }

  public getUpdated(): number {
    return this.updated;
  }

  public wasUpdated(): void {
    this.updated = Date.now();
  }

  public getPaths(iconName: string): any[] {
    return this._icons.find((icon) => icon.properties.name === iconName).icon.paths;
  }

  public setStyles(content: HTMLDivElement): void {
    const borderColor = this.styles.strokeColor || '#fff';
    const borderWeight = this.styles.strokeWeight || 1;
    if (this.styles.fillOpacity > 0) {
      const colorBasedOnFillColor = colorIsDark(this.styles.fillColor)
        ? COLORS['grey-0']
        : '#000000';
      content.style.backgroundColor = this.styles.fillColor
        ? this.styles.fillColor
        : COLORS['base-800'];
      content.style.color = this.styles.overrideColor ? this.color : colorBasedOnFillColor;
      content.style.width = '32px';
      content.style.height = '32px';
    }
    if (this.styles.strokeOpacity > 0) {
      content.style.borderRadius = '50%';
      content.style.border =
        this.styles.strokeOpacity > 0 ? `${borderWeight}px solid ${borderColor}` : 'none';
    }
    if (this.styles.scale && !this._options.isAxonLayer) {
      content.style.transform = `scale(${this.styles.scale})`;
    }

    const status = this.feature?.properties?.attributes?.status?.toLowerCase();
    if (status === 'recording') {
      this.content.classList.add('fs__axon-recording-status');
    } else {
      this.content.classList.remove('fs__axon-recording-status');
    }
  }

  private _toFaSet(fontSet: string): string {
    switch (fontSet) {
      case 'fat':
        return 'fa-thin';
      case 'fal':
        return 'fa-light';
      case 'far':
        return 'fa-regular';
      case 'fas':
        return 'fa-solid';
      case 'fab':
        return 'fa-brands';
      case 'fad':
        return 'fa-duotone';
    }
  }
}
